/**
 * Pestaña TOP-500 del publicador.
 *
 * Muestra las planillas mensuales de los productos más vendidos de Mercado Libre ya
 * cargadas en el sistema, el avance de la búsqueda de cada producto en Amazon, el árbol
 * de rubros de la planilla elegida y el encolado de lo que el usuario seleccione.
 *
 * Decisiones:
 *  - **Una planilla sin fecha de procesado NO está cargada.** La fila de la planilla se
 *    crea ANTES de leer los productos y la fecha se escribe recién al final: si la carga
 *    se corta, queda una planilla con productos a medias que además es la más nueva. Por
 *    eso la que se abre por omisión es la más nueva CON fecha de procesado, y una sin
 *    fecha se muestra diciendo que no está cargada, no con un tablero vacío. Si ninguna
 *    terminó de cargarse no se abre ninguna: el selector pide elegir.
 *  - El progreso que trae la lista de planillas es SOLO el de la más nueva; acá se pide
 *    el de la planilla elegida, que puede ser otra. Mientras la búsqueda está corriendo
 *    se vuelve a preguntar cada minuto.
 *  - `last_ingest` vive en la memoria del proceso del servidor y es una LISTA de
 *    intentos. Se muestran solo los que están corriendo o fallaron: que una carga haya
 *    terminado bien ya se ve porque la planilla aparece en la lista.
 *  - Si una recarga falla, se conserva el tablero anterior y el aviso va arriba: es
 *    preferible un dato de hace un minuto a una pantalla en blanco.
 *  - El buscador se aplica a los subgrupos abiertos: el servidor busca dentro de un
 *    nodo del árbol, no sobre la planilla entera.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Loader2, AlertCircle, RefreshCw, Search, Upload } from 'lucide-react';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { Can } from '../../../lib/Can';
import { FeederFiltersPanel } from '../panels/FeederFiltersPanel';
import { useCan } from '../../../lib/rbac';
import {
  Tree,
  buildHierarchy,
  selectionNodes,
  type Top500Node,
  type VerticalNode,
} from '../top500/Tree';
import {
  EnqueueSummaryModal,
  type EnqueueCommitResult,
  type EnqueuePreview,
} from '../top500/EnqueueSummaryModal';

/** Una planilla mensual (fila de `ml_top_sheets`). */
interface Sheet {
  id: number;
  month: string | null;
  title: string | null;
  run_state: string | null;
  /** Cuándo terminó de cargarse. Vacío = la carga no terminó: la planilla NO sirve. */
  processed_at: string | null;
}

/** Avance de la búsqueda de los productos en Amazon. */
interface Progress {
  pending: number;
  pending_name: number;
  claimed: number;
  resolved: number;
  ambiguous: number;
  not_on_amazon: number;
  error: number;
  total: number;
  resueltos: number;
  run_state?: string | null;
}

/** Cómo terminó el último intento de cargar cada planilla (memoria del servidor). */
interface IngestState {
  state: 'corriendo' | 'ok' | 'error';
  spreadsheet_id: string;
  message: string;
  at: string;
}

interface SheetsResponse {
  sheets: Sheet[];
  latest_sheet_id: number | null;
  /** Progreso de la planilla MÁS NUEVA. Acá se pide el de la elegida, que puede ser otra. */
  progress: Progress | null;
  last_ingest: IngestState[];
}

interface TreeResponse {
  sheet_id: number;
  seller_id: number;
  nodes: Top500Node[];
}

/** Tope de ramas por pedido de encolado (espejo de `MAX_NODES` del servidor). */
const MAX_NODES = 200;
/** Cada cuánto se vuelve a preguntar por el avance mientras la búsqueda corre. */
const PROGRESS_MS = 60_000;
/** Cada cuánto se pregunta si la carga de una planilla ya terminó. */
const INGEST_POLL_MS = 10_000;
/** Cuántas veces como mucho se pregunta por una carga: media hora y se deja de esperar. */
const INGEST_POLL_MAX = (30 * 60_000) / INGEST_POLL_MS;
/** Espera del buscador antes de pedirle nada al servidor. */
const SEARCH_MS = 400;
/** Cuánto queda a la vista el aviso del resultado. */
const BANNER_MS = 8_000;

/** Aviso de resultado (mismo patrón que el descubridor: banner que se va solo). */
interface Banner {
  ok: boolean;
  text: string;
}

/** Lo que devuelve el cliente de la API: nunca lanza en un 4xx, hay que mirar `success`. */
type Respuesta<T> = { success: boolean; data?: T; error?: string };

/**
 * Llamada que NO puede romper la pantalla. El cliente de la API no lanza en un 4xx,
 * pero sí lanza si se corta la red o si la respuesta no es JSON (un servidor que
 * todavía no conoce estas direcciones devuelve una página de error en HTML). Eso queda
 * convertido en un resultado con `success: false` para que salga como aviso.
 */
async function seguro<T>(llamada: Promise<Respuesta<T>>): Promise<Respuesta<T>> {
  try {
    return await llamada;
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export function TopSellersTab() {
  const { t } = useTranslation();
  const sellerId = useUIStore((s) => s.sellerId);
  const sellers = useUIStore((s) => s.sellers);
  const can = useCan();
  const canMoney = can('global.view_money');

  // ── Planillas ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [latestSheetId, setLatestSheetId] = useState<number | null>(null);
  const [lastIngest, setLastIngest] = useState<IngestState[]>([]);
  const [sheetId, setSheetId] = useState<number | null>(null);

  // ── Avance de la búsqueda en Amazon ──
  const [progress, setProgress] = useState<Progress | null>(null);

  // ── Árbol y selección ──
  const [nodes, setNodes] = useState<Top500Node[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<ReadonlySet<string>>(new Set<string>());
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set<number>());
  const [treeToken, setTreeToken] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  // ── Buscador ──
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // ── Carga de una planilla nueva ──
  const [sheetUrl, setSheetUrl] = useState('');
  const [ingestId, setIngestId] = useState<string | null>(null);

  // ── Encolado ──
  const [modalOpen, setModalOpen] = useState(false);
  // Panel de filtros del alimentador: los MISMOS que usa Pipeline (se guardan por cuenta y
  // el encolado desde esta pestaña los lee de la base al confirmar). Cerrado por defecto.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [preview, setPreview] = useState<EnqueuePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  /** Planilla y cuenta con las que se armó el resumen que está a la vista. */
  const contexto = useRef<{ sheetId: number; sellerId: number } | null>(null);

  // El aviso se va solo a los 8 segundos. La cuenta atrás se guarda para poder
  // cancelarla: si llega otro aviso antes, el nuevo no se lleva por delante al viejo, y
  // al salir de la pantalla no queda nada pendiente.
  /**
   * Copia viva del traductor. El sondeo de la carga tiene un presupuesto de media hora y
   * su cuenta de intentos vive dentro del efecto: si `t` estuviera entre sus dependencias,
   * cambiar de idioma reiniciaría el efecto y con él la cuenta, y la pantalla podría
   * quedar preguntando para siempre. Leerlo por referencia da el texto del idioma actual
   * sin volver a armar el sondeo.
   */
  const tRef = useRef(t);
  tRef.current = t;

  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mostrarBanner = useCallback((b: Banner) => {
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    setBanner(b);
    bannerTimer.current = setTimeout(() => setBanner(null), BANNER_MS);
  }, []);
  useEffect(
    () => () => {
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
    },
    []
  );

  // ── Planillas: lectura y aplicación ──

  const aplicarSheets = useCallback((data: SheetsResponse) => {
    setSheets(data.sheets || []);
    setLatestSheetId(data.latest_sheet_id ?? null);
    setLastIngest(data.last_ingest || []);
    // Por omisión se abre la planilla más nueva QUE ESTÉ CARGADA (con fecha de
    // procesado). La lista viene de la más nueva a la más vieja. Si NINGUNA está
    // cargada no se abre ninguna: mostrar una a medias sería mostrar datos incompletos
    // como si fueran la planilla.
    const cargadas = (data.sheets || []).filter((s) => s.processed_at);
    const porOmision = cargadas[0]?.id ?? null;
    // La elegida solo se fija la primera vez: un refresco no le cambia la planilla
    // al usuario por abajo.
    setSheetId((prev) => (prev !== null ? prev : porOmision));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await seguro(
      api.get<SheetsResponse>(`/publisher/top500/sheets?seller_id=${sellerId}`)
    );
    setLoading(false);
    if (!res.success || !res.data) {
      // Se conserva lo que ya estaba a la vista y el aviso va arriba.
      setError(res.error || t('pub.top500.sheetsError'));
      return;
    }
    setError(null);
    aplicarSheets(res.data);
  }, [sellerId, aplicarSheets, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Avance de la planilla elegida ──

  const runStateRef = useRef<string | null>(null);
  runStateRef.current = progress?.run_state ?? null;

  useEffect(() => {
    if (sheetId === null) {
      setProgress(null);
      return;
    }
    let vivo = true;
    const pedir = async () => {
      const res = await seguro(
        api.get<Progress>(`/publisher/top500/progress?sheet_id=${sheetId}`)
      );
      if (!vivo) return;
      if (res.success && res.data) setProgress(res.data);
    };
    void pedir();
    const id = setInterval(() => {
      // Solo se sigue preguntando mientras la búsqueda está corriendo.
      if (runStateRef.current === 'running') void pedir();
    }, PROGRESS_MS);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [sheetId]);

  // ── Árbol de la planilla elegida ──

  const sheet = sheets.find((s) => s.id === sheetId) ?? null;
  const sheetCargada = !!sheet?.processed_at;
  // La más nueva según el servidor (no `sheets[0]`), para avisar si quedó a medias.
  const latestCargada = !!sheets.find((s) => s.id === latestSheetId)?.processed_at;

  useEffect(() => {
    if (sheetId === null || !sheetCargada) {
      setNodes([]);
      return;
    }
    let vivo = true;
    setTreeLoading(true);
    void seguro(
      api.get<TreeResponse>(`/publisher/top500/tree?sheet_id=${sheetId}&seller_id=${sellerId}`)
    ).then((res) => {
      if (!vivo) return;
      setTreeLoading(false);
      if (!res.success || !res.data) {
        // Recarga fallida: se conserva el árbol anterior y se avisa arriba.
        setError(res.error || t('pub.top500.treeError'));
        return;
      }
      setError(null);
      setNodes(res.data.nodes || []);
    });
    return () => {
      vivo = false;
    };
  }, [sheetId, sellerId, sheetCargada, treeToken, t]);

  // Otra planilla o cuenta: la selección anterior ya no significa nada.
  useEffect(() => {
    setSelectedNodes(new Set<string>());
    setSelectedIds(new Set<number>());
  }, [sheetId, sellerId]);

  // ── Buscador con espera ──

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // ── Carga de una planilla nueva ──

  const cargarPlanilla = async () => {
    const url = sheetUrl.trim();
    if (!url) return;
    const res = await seguro(
      api.post<{ started: boolean; spreadsheet_id: string }>('/publisher/top500/ingest', { url })
    );
    if (!res.success || !res.data) {
      mostrarBanner({ ok: false, text: res.error || t('pub.top500.ingestFailed') });
      return;
    }
    setSheetUrl('');
    setIngestId(res.data.spreadsheet_id);
    mostrarBanner({ ok: true, text: t('pub.top500.ingestStarted') });
  };

  /**
   * Mientras una carga corre, se pregunta cada diez segundos si terminó.
   *
   * Dos formas de RENDIRSE, las dos necesarias: el registro del intento vive en la
   * memoria del proceso del servidor, así que un reinicio lo borra y la respuesta deja
   * de nombrar esa planilla para siempre. Sin salida, la pantalla preguntaría cada diez
   * segundos hasta que alguien la cierre y el botón quedaría girando.
   *  - El intento desaparece de la lista → se corta y se dice lo único que se sabe.
   *  - Media hora sin terminar → se corta igual.
   */
  useEffect(() => {
    if (!ingestId) return;
    let intentos = 0;
    const rendirse = () => {
      setIngestId(null);
      mostrarBanner({ ok: false, text: tRef.current('pub.top500.ingestUnknown') });
    };
    const id = setInterval(() => {
      intentos++;
      if (intentos > INGEST_POLL_MAX) {
        rendirse();
        return;
      }
      void seguro(
        api.get<SheetsResponse>(`/publisher/top500/sheets?seller_id=${sellerId}`)
      ).then((res) => {
        if (!res.success || !res.data) return;
        aplicarSheets(res.data);
        const estado = res.data.last_ingest.find((i) => i.spreadsheet_id === ingestId);
        if (!estado) {
          // El servidor ya no sabe nada de este intento (se reinició).
          rendirse();
          return;
        }
        if (estado.state !== 'corriendo') {
          setIngestId(null);
          mostrarBanner({ ok: estado.state === 'ok', text: estado.message });
        }
      });
    }, INGEST_POLL_MS);
    return () => clearInterval(id);
    // `t` va por referencia a propósito (ver `tRef`): cambiar de idioma NO reinicia el
    // presupuesto de media hora del sondeo.
  }, [ingestId, sellerId, aplicarSheets, mostrarBanner]);

  // ── Selección ──

  const verticals: VerticalNode[] = useMemo(
    () => buildHierarchy(nodes, t('pub.top500.ungrouped')),
    [nodes, t]
  );

  const toggleLeaves = useCallback((keys: string[], selected: boolean) => {
    setSelectedNodes((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (selected) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }, []);

  const toggleId = useCallback((id: number, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const limpiarSeleccion = () => {
    setSelectedNodes(new Set<string>());
    setSelectedIds(new Set<number>());
  };

  const sellerNames = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sellers) m.set(s.id, s.nickname);
    return m;
  }, [sellers]);

  // ── Encolado ──

  /** Lo que se le manda al servidor: ramas comprimidas + productos sueltos. */
  const payload = useMemo(
    () => ({
      nodes: selectionNodes(verticals, selectedNodes),
      candidate_ids: [...selectedIds],
    }),
    [verticals, selectedNodes, selectedIds]
  );

  const abrirResumen = async () => {
    if (sheetId === null) return;
    if (payload.nodes.length === 0 && payload.candidate_ids.length === 0) {
      mostrarBanner({ ok: false, text: t('pub.top500.nothingSelected') });
      return;
    }
    if (payload.nodes.length > MAX_NODES) {
      mostrarBanner({
        ok: false,
        text: t('pub.top500.tooManyNodes', { count: payload.nodes.length, max: MAX_NODES }),
      });
      return;
    }

    // Con qué planilla y qué cuenta se armó este resumen. Si el usuario cambia de cuenta
    // con la ventana abierta, lo que dice el resumen ya no es lo que se encolaría: al
    // confirmar se corta en vez de encolar sobre la cuenta equivocada.
    contexto.current = { sheetId, sellerId };

    setModalOpen(true);
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    const res = await seguro(
      api.post<EnqueuePreview>('/publisher/top500/enqueue', {
        sheet_id: sheetId,
        seller_id: sellerId,
        ...payload,
        dry_run: true,
      })
    );
    setPreviewLoading(false);
    if (!res.success || !res.data) {
      setPreviewError(res.error || t('pub.top500.previewError'));
      return;
    }
    setPreview(res.data);
  };

  const confirmar = async () => {
    if (sheetId === null) return;
    // La cuenta o la planilla cambiaron con la ventana abierta: no se encola nada.
    if (contexto.current?.sheetId !== sheetId || contexto.current?.sellerId !== sellerId) {
      setModalOpen(false);
      setPreview(null);
      setPreviewError(null);
      mostrarBanner({ ok: false, text: t('pub.top500.contextChanged') });
      return;
    }
    setConfirming(true);
    const res = await seguro(
      api.post<EnqueueCommitResult>('/publisher/top500/enqueue', {
        sheet_id: sheetId,
        seller_id: sellerId,
        ...payload,
        dry_run: false,
      })
    );
    setConfirming(false);
    if (!res.success || !res.data) {
      setPreviewError(res.error || t('pub.top500.previewError'));
      return;
    }

    const r = res.data;
    // Una tanda puede fallar sin que falle el pedido entero: eso viaja en `errors`.
    const errores = r.errors ?? [];
    setModalOpen(false);
    setPreview(null);
    limpiarSeleccion();
    const texto = t('pub.top500.enqueueDone', {
      queued: r.queued ?? 0,
      duplicates: r.duplicates ?? 0,
      family: r.family_enqueued ?? 0,
    });
    // Keepa cortó por cuota: se encoló igual, pero faltaron variantes o fichas. Se avisa.
    const conCorte = r.keepa_budget_stop ? `${texto} — ${t('pub.top500.keepaBudgetStop')}` : texto;
    mostrarBanner({
      ok: errores.length === 0 && !r.keepa_budget_stop,
      text:
        errores.length > 0
          ? `${conCorte} — ${t('pub.top500.enqueueErrors', { detail: errores.join('; ') })}`
          : conCorte,
    });
    // El árbol y los subgrupos abiertos cambiaron: lo encolado pasa a gris.
    setTreeToken((n) => n + 1);
    setReloadToken((n) => n + 1);
  };

  // ── Vista ──

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.resueltos / progress.total) * 100))
      : 0;
  const runState = progress?.run_state ?? sheet?.run_state ?? null;
  const seleccionados = selectedNodes.size + selectedIds.size;
  // Solo interesan los intentos que están corriendo o que fallaron: que una carga haya
  // terminado bien ya se ve porque la planilla aparece en la lista.
  const avisosIngesta = lastIngest.filter((i) => i.state !== 'ok');

  return (
    <div className="space-y-4">
      {/* Filtros de la cuenta (compartidos con Pipeline): precio, rating, peso, países, variantes */}
      <FeederFiltersPanel open={filtersOpen} onToggle={() => setFiltersOpen((v) => !v)} />
      {/* Encabezado: mes de la planilla, selector, avance, carga y buscador */}
      <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Trophy size={18} className="text-[var(--color-primary)]" />
          <h2 className="text-sm font-semibold text-[var(--color-text)]">
            {t('pub.topsellers')}
            {sheet && (
              <span className="ml-2 font-normal text-[var(--color-text-secondary)]">
                {sheet.month || sheet.title || `#${sheet.id}`}
              </span>
            )}
          </h2>

          {sheets.length > 0 && (
            <select
              value={sheetId ?? ''}
              onChange={(e) => setSheetId(e.target.value ? Number(e.target.value) : null)}
              aria-label={t('pub.top500.chooseSheet')}
              className="px-2 py-1 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]"
            >
              {/* Ninguna planilla terminada de cargar: no se elige ninguna sola. */}
              {sheetId === null && <option value="">{t('pub.top500.chooseSheet')}</option>}
              {sheets.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.month || s.title || `#${s.id}`) +
                    (s.processed_at ? '' : ` — ${t('pub.top500.notLoaded')}`)}
                </option>
              ))}
            </select>
          )}

          <div className="ml-auto flex items-center gap-3">
            {progress && progress.total > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-28 h-1.5 rounded-full bg-[var(--color-border-light)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-primary)]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {t('pub.top500.progress', {
                    done: progress.resueltos,
                    total: progress.total,
                  })}
                </span>
                {runState && (
                  <span className="text-[11px] text-[var(--color-text-tertiary)]">
                    {t(`pub.top500.runState.${runState}`, runState)}
                  </span>
                )}
              </div>
            )}
            <button
              onClick={() => void load()}
              disabled={loading}
              aria-label={t('pub.top500.refreshLabel')}
              title={t('pub.top500.refreshLabel')}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
            <Search size={14} className="text-[var(--color-text-tertiary)]" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('pub.top500.searchPlaceholder')}
              className="flex-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]"
            />
          </div>

          <Can cap="publisher.topsellers_enqueue">
            <div className="flex items-center gap-1.5">
              <input
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder={t('pub.top500.sheetUrl')}
                className="px-2 py-1 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] w-52"
              />
              <button
                onClick={() => void cargarPlanilla()}
                disabled={!sheetUrl.trim() || !!ingestId}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
              >
                {ingestId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Upload size={13} />
                )}
                {t('pub.top500.loadSheet')}
              </button>
            </div>
          </Can>
        </div>

        {!loading && sheets.length === 0 && !error && (
          <p className="text-xs text-[var(--color-text-secondary)]">{t('pub.top500.notLoaded')}</p>
        )}

        {sheet && !sheetCargada && (
          <p className="text-xs text-[var(--color-warning)]">{t('pub.top500.sheetNotLoaded')}</p>
        )}

        {/* La planilla más nueva quedó a medias y se está mirando otra: conviene decirlo. */}
        {latestSheetId !== null && latestSheetId !== sheetId && !latestCargada && (
          <p className="text-xs text-[var(--color-text-tertiary)]">
            {t('pub.top500.latestNotLoaded')}
          </p>
        )}

        {/* Cargas lanzadas desde la pantalla (viven en la memoria del servidor). */}
        {avisosIngesta.map((ing) => (
          <p
            key={ing.spreadsheet_id}
            className={`text-xs ${
              ing.state === 'error'
                ? 'text-[var(--color-error)]'
                : 'text-[var(--color-text-secondary)]'
            }`}
          >
            {ing.state === 'corriendo'
              ? t('pub.top500.ingestStarted')
              : `${t('pub.top500.ingestFailed')}: ${ing.message}`}
          </p>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--color-error)] text-[var(--color-error)] text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {banner && (
        <div
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs border ${
            banner.ok
              ? 'bg-green-900/10 border-green-700/20 text-green-400'
              : 'bg-red-900/10 border-red-700/20 text-red-400'
          }`}
        >
          <span className="text-base">{banner.ok ? '✓' : '✕'}</span>
          <div className="flex-1">{banner.text}</div>
          <button
            onClick={() => setBanner(null)}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] text-lg"
          >
            {'×'}
          </button>
        </div>
      )}

      {/* Barra de acción: qué hay elegido y el botón de encolar */}
      {sheetCargada && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-[var(--color-text-secondary)]">
            {t('pub.top500.selection', {
              nodes: selectedNodes.size,
              items: selectedIds.size,
            })}
          </span>
          {seleccionados > 0 && (
            <button
              onClick={limpiarSeleccion}
              className="text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] underline"
            >
              {t('pub.top500.clearSelection')}
            </button>
          )}
          <Can cap="publisher.topsellers_enqueue">
            <button
              onClick={() => void abrirResumen()}
              disabled={seleccionados === 0}
              className="ml-auto px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] text-white disabled:opacity-50"
            >
              {t('pub.top500.enqueueSelected')}
            </button>
          </Can>
        </div>
      )}

      {treeLoading && nodes.length === 0 && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] px-1">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      )}

      {sheetId !== null && sheetCargada && (
        <Tree
          sheetId={sheetId}
          sellerId={sellerId}
          search={search}
          verticals={verticals}
          selectedNodes={selectedNodes}
          onToggleLeaves={toggleLeaves}
          selectedIds={selectedIds}
          onToggleId={toggleId}
          reloadToken={reloadToken}
          canMoney={canMoney}
          sellerNames={sellerNames}
        />
      )}

      {modalOpen && (
        <EnqueueSummaryModal
          preview={preview}
          loading={previewLoading}
          confirming={confirming}
          error={previewError}
          onCancel={() => {
            if (confirming) return;
            setModalOpen(false);
            setPreview(null);
            setPreviewError(null);
          }}
          onConfirm={() => void confirmar()}
        />
      )}
    </div>
  );
}
